import { useEffect, useMemo, useState } from 'react';

import {
  Animate,
  Assets,
  Button,
  ButtonTextOrIcon,
  Feedback,
  Form,
  SpeechBubble,
  Text,
  useDatePickerInput,
  UseDatePickerInputArgs,
  VerticalSpacer,
} from '@foradevelopment/fora-ui';
import {
  EmploymentPeriodInput,
  EmploymentPeriodViewModel,
  EmploymentViewModelNew,
  MarkupInput,
  MarkupNew,
  UpdateEmploymentInput,
} from '@fora-monorepo/employer-my-pages-common/types';
import { trackCustomEvent } from '@fora-monorepo/webapp-analytics';
import { isAfter } from '@fora-monorepo/date-utils';

import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { addOneDay, getMarkupStartDateRangeMax, getMarkupStartDateRangeMin, subOneDay } from '../utils';
import {
  validateEmploymentPeriodEndDate,
  validateEmploymentPeriodStartDate,
  validateMarkupList,
  validateMarkupStartDate,
} from '../../common/utils';
import { EmploymentFormStepWrapper, EmploymentPeriodForm } from '../../components';
import { useTypedTranslation } from '../../i18n/i18n';
import { ExtendedMarkupType, MarkupEditList } from '../../components/MarkupEditList';
import { updateEmployment } from '../../redux/employment/employmentThunks';
import { envConfig } from '../../config';

export const EditEmploymentPeriodForm = ({
  goToConfirmationPanel,
  employment,
  isFirstEmploymentPeriod,
  employmentPeriodIndex,
}: {
  goToConfirmationPanel: (employment: EmploymentViewModelNew) => void;
  editEmployeeInputData: UpdateEmploymentInput;
  employment: EmploymentViewModelNew;
  // TODO: Remove when the start date of the first employment period becomes editable.
  isFirstEmploymentPeriod?: boolean;
  employmentPeriodIndex: number;
}) => {
  const { employeeId } = employment.employee;
  const employmentPeriod = employment.employmentPeriods?.[employmentPeriodIndex];
  const { t } = useTypedTranslation();
  const dispatch = useAppDispatch();
  const { faGroups, insuranceAgreementValidFromDate } = useAppSelector(state => state.employer.employerInfo);
  const monthlyReportingReleaseDate = useAppSelector(state => state.foraGlobal.settings.monthlyReportingReleaseDate);
  const [submitHasBeenClicked, setSubmitHasBeenClicked] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showNoChangesMadeMessage, setShowNoChangesMadeMessage] = useState(false);

  const [shouldShowInfoAboutMarkup, setShouldShowInfoAboutMarkup] = useState(false);
  const [extendedMarkupList, setExtendedMarkupList] = useState<ExtendedMarkupType[]>(
    employmentPeriod?.markups.map(markup => ({
      ...markup,
      newMarkup: undefined,
      deletedMarkup: false,
      fokParameterChoices: [],
    })) ?? []
  );

  const [errorSubmittingMessage, setErrorSubmittingMessage] = useState<string[]>([]);
  const [submittingForm, setSubmittingForm] = useState(false);

  // EmploymentPeriodForm
  const startEmploymentInput = useDatePickerInput({
    initValue: employmentPeriod?.employmentStartDate,
  });
  const endEmploymentInput = useDatePickerInput({
    initValue: employmentPeriod?.employmentEndDate ?? '',
  });

  const employmentPeriodBefore = employment.employmentPeriods[employmentPeriodIndex - 1];
  const employmentPeriodAfter = employment.employmentPeriods[employmentPeriodIndex + 1];

  startEmploymentInput.props.minDate = employmentPeriodBefore?.employmentEndDate
    ? addOneDay(employmentPeriodBefore?.employmentEndDate)
    : '';
  startEmploymentInput.props.maxDate = endEmploymentInput.value ?? '';
  endEmploymentInput.props.minDate = startEmploymentInput.value ?? '';
  endEmploymentInput.props.maxDate = employmentPeriodAfter?.employmentStartDate
    ? subOneDay(employmentPeriodAfter?.employmentStartDate)
    : '';
  endEmploymentInput.props.disabled = !startEmploymentInput.value && !endEmploymentInput.value;

  useEffect(() => {
    if (submitHasBeenClicked) {
      const markupErrors = validateMarkupList(
        mapExtendedToMarkup(extendedMarkupList),
        startEmploymentInput.value,
        endEmploymentInput.value,
        faGroups
      );
      setErrorSubmittingMessage(markupErrors);
    }
    // Omit submitHasBeenClicked from the dependency array to avoid infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endEmploymentInput.value, extendedMarkupList, startEmploymentInput.value]);

  // TODO: Remove when the start date of the first employment period becomes editable.
  const getEarliestStartDatePlusOneDay = (markupList: typeof extendedMarkupList): string | undefined => {
    const startDates = markupList.map(markup => markup.startDate);

    if (startDates.length === 0) {
      return undefined;
    }
    const earliestDate = startDates.reduce((earliest, current) => (current < earliest ? current : earliest));
    const [year, month, day] = earliestDate.split('-').map(Number);
    const nextDay = new Date(year, month - 1, day + 1); // Använd Date för att hantera övergångar

    const nextDayFormatted = `${nextDay.getFullYear()}-${String(nextDay.getMonth() + 1).padStart(2, '0')}-${String(nextDay.getDate()).padStart(2, '0')}`;

    return nextDayFormatted;
  };

  const earliestStartDatePlusOneDay = isFirstEmploymentPeriod
    ? getEarliestStartDatePlusOneDay(extendedMarkupList)
    : undefined;

  const markupDatePickerInputSettings = useMemo((): UseDatePickerInputArgs => {
    const employeeIdIsValid = employeeId.length === 0 && employeeId.length;
    const employeeIdArg = employeeIdIsValid ? employeeId : undefined;
    return {
      validate: validateMarkupStartDate(
        true,
        monthlyReportingReleaseDate,
        faGroups,
        employeeIdArg,
        startEmploymentInput.value,
        endEmploymentInput.value
      ),
      minDate: getMarkupStartDateRangeMin(
        monthlyReportingReleaseDate,
        insuranceAgreementValidFromDate,
        employeeIdArg,
        startEmploymentInput.value,
        // TODO: Remove when the start date of the first employment period becomes editable.
        earliestStartDatePlusOneDay
      ),
      maxDate: getMarkupStartDateRangeMax(employeeIdArg, endEmploymentInput.value),
    };
  }, [
    employeeId,
    monthlyReportingReleaseDate,
    faGroups,
    startEmploymentInput.value,
    insuranceAgreementValidFromDate,
    endEmploymentInput.value,
    earliestStartDatePlusOneDay,
  ]);

  const haveEmploymentPeriodInputChanged = (
    employmentPeriods: EmploymentPeriodViewModel[],
    employmentPeriodInputs?: EmploymentPeriodInput
  ): boolean => {
    if (!employmentPeriodInputs) return false;

    if (employmentPeriods[employmentPeriodIndex].markups.length !== employmentPeriodInputs.markups.length) return true;
    const currentPeriod = employmentPeriods[employmentPeriodInputs.index];
    const startDateChanged = currentPeriod.employmentStartDate !== employmentPeriodInputs.employmentStartDate;
    const endDateChanged = currentPeriod.employmentEndDate !== (employmentPeriodInputs.employmentEndDate || null);

    const markupsChanged =
      currentPeriod.markups.length !== employmentPeriodInputs.markups.length ||
      currentPeriod.markups.some((currentMarkup, i) => {
        const inputMarkup = employmentPeriodInputs.markups[i];

        return currentMarkup.startDate !== inputMarkup.startDate || currentMarkup.fokId !== inputMarkup.fokId;
      });

    return startDateChanged || endDateChanged || markupsChanged;
  };

  const submitAddEmployment = async () => {
    setSubmitHasBeenClicked(true);
    setSubmittingForm(true);
    const errorMessages: string[] = [];
    setErrorSubmittingMessage([]);

    // Markup form is editing
    if (isEditing) {
      setErrorSubmittingMessage(['Error! Någon uppmärkning håller på att redigeras']);
      setSubmittingForm(false);
      return;
    }

    // Employment period start
    const startValidationErrors = validateEmploymentPeriodStartDate(endEmploymentInput.value)(
      startEmploymentInput.value
    );
    errorMessages.push(...startValidationErrors);
    startEmploymentInput.props.errors = startValidationErrors;

    // Employment period end
    const endValidationErrors = validateEmploymentPeriodEndDate(startEmploymentInput.value)(endEmploymentInput.value);
    errorMessages.push(...endValidationErrors);
    endEmploymentInput.props.errors = endValidationErrors;

    const newMarkupList = mapExtendedToMarkup(extendedMarkupList);

    const markupErrors = validateMarkupList(
      newMarkupList,
      startEmploymentInput.value,
      endEmploymentInput.value,
      faGroups
    );

    const formValid = errorMessages.length === 0 && markupErrors.length === 0;

    if (!formValid) {
      setErrorSubmittingMessage(markupErrors);
      setSubmittingForm(false);
      return;
    }

    const employmentInputData = toUpdateEmploymentInput({
      employment,
      employmentPeriodIndex,
      employmentStartDate: startEmploymentInput.value,
      employmentEndDate: endEmploymentInput.value,
      newMarkups: newMarkupList,
    });

    const employmentPeriodHasChanged = haveEmploymentPeriodInputChanged(
      employment.employmentPeriods,
      employmentInputData.employmentPeriod
    );

    if (!employmentPeriodHasChanged) {
      setShowNoChangesMadeMessage(true);
      new Promise(resolve => setTimeout(resolve, 4000)).then(() => setShowNoChangesMadeMessage(false));
      setSubmittingForm(false);
      return;
    }

    const result = await dispatch(
      updateEmployment({ unchangedEmployment: employment, updateEmploymentInput: employmentInputData })
    );

    if (updateEmployment.fulfilled.match(result)) {
      const updatedEmployment = result.payload;
      trackCustomEvent('Edited employment period');
      goToConfirmationPanel(updatedEmployment);
    }
    if (updateEmployment.rejected.match(result)) {
      setErrorSubmittingMessage(
        envConfig.devLogging && result.payload && result.payload.message
          ? [`Fel i backend: ${result.payload.message}`]
          : [t('errorMessages.contactSupport')]
      );
      setSubmittingForm(false);
    }
  };

  useEffect(() => {
    if (startEmploymentInput.isDirty()) {
      const hasOnlyDeletedMarkups = extendedMarkupList.every(markup => markup.deletedMarkup);

      const markupStartDates = extendedMarkupList
        .filter(markup => hasOnlyDeletedMarkups || !markup.deletedMarkup)
        .map(markup => markup.newMarkup?.startDate || markup.startDate);

      const startDateIsAfterMarkup = markupStartDates.some(startDate => isAfter(startEmploymentInput.value, startDate));

      setShouldShowInfoAboutMarkup(startDateIsAfterMarkup);
    }
  }, [extendedMarkupList, startEmploymentInput]);

  return (
    <Form preventDefault backgroundColor="transparent">
      <EmploymentFormStepWrapper
        heading={t('form.employmentPeriod.title')}
        ingress={t('form.employmentPeriod.description')}
        isFirstStep
      >
        <EmploymentPeriodForm
          startDateInputProps={startEmploymentInput.props}
          endDateInputProps={endEmploymentInput.props}
          // TODO: Remove when the start date of the first employment period becomes editable.
          disabled={isFirstEmploymentPeriod}
        />
        {shouldShowInfoAboutMarkup ? (
          <>
            <VerticalSpacer marginBottom={20} />
            <SpeechBubble variant="informative" negativeSideMargins={false}>
              <Assets.CircleExclamationIcon />
              <Text>{t('form.employmentPeriod.infoAboutMarkup')}</Text>
            </SpeechBubble>
          </>
        ) : null}
      </EmploymentFormStepWrapper>

      <EmploymentFormStepWrapper
        heading={t('form.editEmployment.markupTitle')}
        ingress={t('form.markup.addMarkupDescription')}
      >
        <MarkupEditList
          extendedMarkupList={extendedMarkupList}
          setExtendedMarkupList={setExtendedMarkupList}
          markupOptions={faGroups}
          markupDatePickerInputSettings={markupDatePickerInputSettings}
          faGroups={faGroups}
          setIsEditing={setIsEditing}
          // TODO: Remove when the start date of the first employment period becomes editable.
          isFirstEmploymentPeriod={isFirstEmploymentPeriod}
        />
      </EmploymentFormStepWrapper>
      {!!errorSubmittingMessage.length && (
        <Feedback type="error" marginBottom={40} negativeSideMargins testId="error-submitting-message">
          {errorSubmittingMessage[0]}
        </Feedback>
      )}
      <Animate.Height isTriggered={showNoChangesMadeMessage}>
        {showNoChangesMadeMessage && (
          <Feedback type="warning" size="slim" marginBottom={40}>
            {t('form.editEmployee.noChangesMade')}
          </Feedback>
        )}
      </Animate.Height>
      <Button type="button" onClick={submitAddEmployment} disabled={submittingForm} fullWidth={false}>
        <ButtonTextOrIcon
          icon={<Assets.Spinner color="white" width={20} center={false} />}
          textColor="white"
          isLoading={submittingForm}
        >
          {t('common.save')}
        </ButtonTextOrIcon>
      </Button>
    </Form>
  );
};

export const mapExtendedToMarkup = (extendedList: ExtendedMarkupType[]): MarkupNew[] => {
  return extendedList
    .filter(item => !item.deletedMarkup)
    .map(item => {
      const markup = item.newMarkup || item;

      const markupNew = {
        fokId: markup.fokId,
        fokText: markup.fokText,
        startDate: markup.startDate,
        endDate: markup.endDate,
        faGroupId: markup.faGroupId,
      };

      return markupNew as MarkupNew;
    });
};

const toUpdateEmploymentInput = ({
  employment,
  employmentPeriodIndex,
  employmentStartDate,
  employmentEndDate,
  newMarkups,
}: {
  employment: EmploymentViewModelNew;
  employmentPeriodIndex: number;
  employmentStartDate: string;
  employmentEndDate: string;
  newMarkups: MarkupNew[];
}): UpdateEmploymentInput => {
  const { partnerNumberIndividual } = employment.employee;

  return {
    partnerNumberIndividual: partnerNumberIndividual,
    employmentPeriod: {
      index: employmentPeriodIndex,
      employmentStartDate,
      employmentEndDate,
      markups: newMarkups.map(
        markup =>
          ({
            startDate: markup.startDate,
            fokId: markup.fokId,
          }) as MarkupInput
      ),
      fokParameterChoices: employment.employmentPeriods[employmentPeriodIndex].fokParameterChoices.map(
        fokParameterChoice => ({
          ...fokParameterChoice,
          startDate: fokParameterChoice.startDate ?? undefined,
          endDate: fokParameterChoice.endDate ?? undefined,
        })
      ),
    },
  };
};
